import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Type } from 'class-transformer';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import backToListView from 'src/actions/shared/backToListView';
import createDocumentResource, { handleStandardErrors } from 'src/actions/shared/createDocumentResource';
import generateId from 'src/lib/generateId';
import validateSiteConfigAnswersCount from './validateSiteConfigAnswersCount';
import validateType, { translateErrors } from 'src/adapters/validateType';
import { Matches, ValidateNested, ArrayMinSize, ArrayMaxSize } from 'class-validator';
import { toastError, toastSuccess } from 'src/actions/toast';

export class HelpCenter {
  @ValidateNested({ each: true })
  @Type(() => SiteConfig)
  siteConfigs: SiteConfig[];
}

export class SiteConfig {
  id: string;
  @Matches(/\S/, { message: 'Please name your Help Center' })
  name: string;
  @Matches(/\S/, { message: 'You must specify a language' })
  lng: string;
  audienceId: string;
  numColumns: number;
  mainTitle: string;
  maxRows: number;
  faqHeading: string;
  backLink: string;
  moreResultsLink: string;
  searchPlaceholder: string;
  searchResultHeadingTemplate: string;
  seeMoreLinkText: string;
  @ValidateNested({ each: true })
  @Type(() => Section)
  sections: Section[];
}

const HELP_CENTER_PUT_TIMEOUT = 15000; // 15 seconds

export class Section {
  id: string;

  @Matches(/\S/, { message: 'Please provide a section name' })
  header: string;
  @ArrayMinSize(1, { message: 'Please select at least one answer' })
  @ArrayMaxSize(1000, { message: 'Maximum number of answers 1000' })
  answerIds: string[];
}

export const fetchSuccess = (state, action) => {
  state.loadingState = 'succeeded';
  const configs = action.payload.siteConfigs;
  configs.forEach(config => {
    const sections = config.sections;
    sections.forEach(section => {
      if (!section.id) {
        section.id = generateId();
      }
    });
  });
  state.value = action.payload;
};

const helpCenterResource = createDocumentResource<HelpCenter>({
  name: 'helpCenter',
  endpoint: 'v1/orgs/:orgId/configuration/help-center',
  type: SiteConfig, // This is non standard (because it is validating on the type inside of the resource not the whole resource)
  reducers: {
    fetchSuccess,
  },
  initialValue: { siteConfigs: [] },
});

const replaceSiteConfig: sharedTypes.AppThunk =
  (siteConfig: SiteConfig) =>
  async (dispatch, getState, { gateways }) => {
    const { updateStarted, updateSuccess, updateError, updateRequestError } = helpCenterResource.actions;

    if (!(await dispatch(validateSiteConfigAndAnswers(siteConfig)))) {
      return false;
    }

    try {
      dispatch(updateStarted({}));

      let siteConfigs: any = cloneDeep(get(getState(), 'helpCenter.value.siteConfigs', []));
      let config = siteConfig;

      const isNew = !get(siteConfig, 'id');
      if (isNew) {
        config = { ...config, id: generateId() };
      } else {
        siteConfigs = filter(siteConfigs, siteConfig => siteConfig.id !== config.id);
      }
      siteConfigs.push(config);

      await gateways.helpCenter.replace({}, { siteConfigs }, { timeout: HELP_CENTER_PUT_TIMEOUT });
      dispatch(updateSuccess({}));
      const successMessage = `${isNew ? 'Created' : 'Updated'} ${
        siteConfig.name
      }. It may take up to a minute for your changes to take effect.`;
      dispatch(toastSuccess(successMessage));

      backToListView();

      return true;
    } catch (error) {
      handleStandardErrors({ error, dispatch, handleDomainError: updateError, handleDefaultError: updateRequestError });
      dispatch(toastError(`There was an error while updating`));
      return false;
    }
  };

const remove: sharedTypes.AppThunk =
  (itemId: string) =>
  async (dispatch, getState, { gateways }) => {
    const { updateStarted, updateSuccess, updateError, updateRequestError } = helpCenterResource.actions;

    try {
      dispatch(updateStarted({}));

      let siteConfigs = cloneDeep(get(getState(), 'helpCenter.value.siteConfigs', []));

      siteConfigs = filter(siteConfigs, siteConfig => siteConfig.id !== itemId);
      await gateways.helpCenter.replace({}, { siteConfigs });
      dispatch(updateSuccess({}));
      dispatch(toastSuccess('Remove successful!'));

      return true;
    } catch (error) {
      handleStandardErrors({ error, dispatch, handleDomainError: updateError, handleDefaultError: updateRequestError });
      dispatch(toastError(`There was an error while removing`));
      return false;
    }
  };

const validateSiteConfigAndAnswers = (siteConfig: SiteConfig) => async dispatch => {
  const { validationError } = helpCenterResource.actions;
  let errors;

  try {
    validateType(SiteConfig, siteConfig);
  } catch (err) {
    errors = [...translateErrors((err as any).errors)];
  }

  try {
    validateSiteConfigAnswersCount(siteConfig);
  } catch (err) {
    errors = [...(errors || []), ...translateErrors((err as any).errors)];
  }
  if (!isEmpty(errors)) {
    dispatch(validationError(errors));
    return false;
  }

  return true;
};

helpCenterResource.effects.replaceSiteConfig = replaceSiteConfig;
helpCenterResource.effects.remove = remove;

export default helpCenterResource;
